import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { API, axios, ensureAbsoluteUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { classi } from '../components/accesso/campi';
import {
  formatEuro, titoloImmobile, leggiData, ora,
  linkWhatsApp, indirizzoCompleto, linkIndicazioni, motivoScartoBreve, daCompilare,
  adessoRoma, chiedeEsito, attendeNuovoOrario,
} from '../utils/percorso';
import {
  prezzoMq, prezzoNumero, prezzoDaVerificare, formatEuroMq, fotoImmobile, provenienza, statoAgenzia, TESTI_AGENZIA,
  storia, esitoVisita, propostaAttiva, puoChiedereVisita, stimaLocale, inseritoDalloStaff, etichettaCliente, daDecidereOra,
  bloccoNonInteressa, fuoriRicerca, offerteProposta, avvisoScadenza, quandoParere, controproposta, testoControproposta, testoAgenzia,
  altriPortali,
} from '../utils/immobili';
import { conAnno, indicazioni, quandoVisita } from '../utils/visite';
import { useTraduzioni, contesto, PRINCIPALE } from '../i18n';
import Icona from '../components/cliente/Icone';
import { FotoConsulente } from '../components/cliente/SezioniHome';
import { Sposta } from '../components/cliente/CardOggi';
import { useConsulente } from '../components/cliente/useConsulente';
import { DecisioneScheda, RispostaImmobile } from '../components/cliente/immobili/Decisione';
import GalleriaFoto from '../components/features/GalleriaFoto';
import { usePrezzoZona } from '../components/features/PrezzoZona';
import { testoOmi, variazioniPrezzo } from '../utils/prezzoZona';
import { DatiImmobile, Dintorni } from '../components/cliente/immobili/DatoImmobile';
import '../components/cliente/immobili.css';

// Scheda di un immobile per il cliente (/immobili/:id): bozza design/immobili-v3.html, vista "Scheda".
// Se una rilettura non riesce (dopo un'azione) i dati di prima restano a video con un avviso; se il server
// rifiuta un'azione (400: la casa è cambiata, es. da un'altra finestra) si rileggono i dati e si dice perché.

const MESI_BREVI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic']; // date delle visite
// il mese breve nella lingua del cliente ("set", "сент.")
const meseBreve = (d, lingua) => (lingua === PRINCIPALE ? MESI_BREVI[d.getMonth()]
  : contesto(lingua).data(d, { month: 'short', timeZone: undefined }));
const OGNI_MEZZO_MINUTO = 30 * 1000;

// Foto della visita: private, si leggono con il login
const MiniatureVisita = ({ visita }) => {
  const { l: lingua } = useTraduzioni();
  const [url, setUrl] = useState([]);
  useEffect(() => {
    let attivo = true;
    const creati = [];
    Promise.all((visita.foto || []).map((f) => axios.get(`${API}/visite/${visita.id}/foto/${f.id}`, { responseType: 'blob' })
      .then((r) => { const u = URL.createObjectURL(r.data); creati.push(u); return u; }).catch(() => null)))
      .then((tutti) => { if (attivo) setUrl(tutti.filter(Boolean)); });
    return () => { attivo = false; creati.forEach((u) => URL.revokeObjectURL(u)); };
  }, [visita.id, visita.foto]);
  if (!url.length) return null;
  return <div className="th">{url.map((u) => <a key={u} href={u} target="_blank" rel="noopener noreferrer"><img src={u} alt={contesto(lingua).t('immobili.scheda.foto_visita')} /></a>)}</div>;
};

// La vostra proposta: le offerte in fila (la vostra, la controproposta del venditore, …) e la scadenza. Date nell'ora
// di Roma (offerteProposta): prima l'ora del telefono, e a Tokyo l'offerta della sera era datata il giorno dopo
const NOMI_OFFERTE_CLIENTE = { offerta: 'offerta', controproposta: 'controproposta', rilancio: 'rilancio',
  accettata: 'accettata', rifiutata: 'rifiutata' };
// Sotto il nome dell'offerta: importo e giorno, uniti solo se ci sono tutti e due (prima « · 15 set» per la risposta
// del venditore, che non ha un importo)
export const rigaOfferta = (o, lingua = PRINCIPALE) => [
  ['accettata', 'rifiutata'].includes(o.tipo) ? null : formatEuro(prezzoNumero({ prezzo_testo: o.prezzo }), lingua), o.quando,
].filter(Boolean).join(' · ');
export const LaVostraProposta = ({ proposte, immobileId, consulente, adesso }) => {
  const { t, l } = useTraduzioni();
  const p = [...(proposte || [])].filter((x) => x.immobile_id === immobileId && (x.storico_offerte || []).length).pop();
  if (!p) return null;
  // l'ultima voce è la controproposta del venditore: tocca a voi (prima "Il venditore risponde entro venerdì 18")
  const risposta = controproposta(p, l);
  // la scadenza con la regola della home (avvisoScadenza): quella già passata non si annuncia al futuro, si dice
  // che è passata
  const scade = risposta ? null : avvisoScadenza(p, adesso, l);
  const nome = consulente?.nome || t('comune.il_consulente');
  return (
    <section className="card r" data-testid="la-vostra-proposta">
      <div className="ttl"><div className="i"><Icona nome="euro" /></div><h2>{t('immobili.scheda.proposta_titolo')}</h2></div>
      {risposta && <p style={{ fontSize: '.86rem', color: 'var(--mute)', marginBottom: '.6rem' }} data-testid="controproposta">{testoControproposta(risposta, consulente?.nome, l)}</p>}
      {scade && !scade.passata && (
        <p style={{ fontSize: '.86rem', color: 'var(--mute)', marginBottom: '.6rem' }}>
          {t('immobili.scheda.venditore_entro', { quando: scade.quando })}
        </p>
      )}
      {scade?.passata && (
        <p style={{ fontSize: '.86rem', color: 'var(--mute)', marginBottom: '.6rem' }} data-testid="scadenza-passata">
          {t('immobili.scheda.scadenza_passata', { quando: scade.quando, nome })}
        </p>
      )}
      <div className="rows">
        {offerteProposta(p, l).map((o, i) => (
          <div key={i}>
            <span className="num">{o.giorno ?? '·'}</span>
            <div>
              <b>{NOMI_OFFERTE_CLIENTE[o.tipo] ? t(`immobili.scheda.${NOMI_OFFERTE_CLIENTE[o.tipo]}`) : o.tipo}</b>
              {rigaOfferta(o, l) && <span>{rigaOfferta(o, l)}</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const Parere = ({ immobile, consulente, adesso }) => {
  const { t, l } = useTraduzioni();
  const pro = immobile.parere_pro || [];
  const contro = immobile.parere_contro || [];
  if (!immobile.parere && !pro.length && !contro.length) return null;
  const autore = immobile.parere_autore_nome || consulente.nome;
  const quando = quandoParere(immobile, adesso, l);
  return (
    <section className="card hero parere r" data-testid="parere">
      <div className="ttl"><div className="i"><Icona nome="cuore" /></div><h2>{t('immobili.scheda.parere', { nome: autore })}</h2></div>
      {immobile.parere && <p>{immobile.parere}</p>}
      {(pro.length > 0 || contro.length > 0) && (
        <div className="pn">
          {pro.length > 0 && <div className="si"><b>{t('immobili.scheda.perche_si')}</b><ul>{pro.map((v) => <li key={v}>{v}</li>)}</ul></div>}
          {contro.length > 0 && <div className="no"><b>{t('immobili.scheda.da_valutare')}</b><ul>{contro.map((v) => <li key={v}>{v}</li>)}</ul></div>}
        </div>
      )}
      <div className="firma">
        <FotoConsulente consulente={{ ...consulente, nome: autore }} />
        {autore}{quando ? ` · ${t('immobili.scheda.aggiornato', { quando })}` : ''}
      </div>
    </section>
  );
};

// Indicazioni e Sposta sulla visita fissata: gli stessi pezzi della riga della card (RigaVisitaCliente)
const AzioniVisita = ({ visita, immobile, consulente, onFatto, onRifiuto }) => {
  const { t } = useTraduzioni();
  const [sposta, setSposta] = useState(false);
  const [chiesto, setChiesto] = useState(false);
  const richiesta = chiesto || !!visita.spostamento_richiesto;
  return (
    <div className="azioni-visita" data-testid="azioni-visita">
      <a href={indicazioni(immobile)} target="_blank" rel="noopener noreferrer">{t('comune.indicazioni')}</a>
      <button type="button" className="link" onClick={() => setSposta(!sposta)} disabled={richiesta} aria-expanded={sposta}>
        {t(richiesta ? 'comune.spostamento_chiesto' : 'comune.sposta')}
      </button>
      {sposta && !richiesta && (
        <Sposta visita={visita} consulente={consulente?.nome ? consulente : { nome: t('comune.il_consulente') }}
          onRichiesta={() => { setChiesto(true); setSposta(false); onFatto(); }}
          onRifiuto={(messaggio) => { setSposta(false); onRifiuto(messaggio); }} />
      )}
    </div>
  );
};

// Sotto una visita passata senza esito: "Diteci com'è andata" solo se la home lo chiede davvero (stessa regola della
// card "Com'è andata?": ultimi 30 giorni, casa non scartata, nessuno spostamento chiesto); se il cliente aveva
// chiesto di spostarla, la visita non c'è stata; altrimenti l'esito non è stato dato.
const SenzaEsito = ({ visita, immobile, adesso }) => {
  const { t } = useTraduzioni();
  if (attendeNuovoOrario(visita, adesso)) return <span data-testid="esito-scheda">{t('immobili.scheda.esito_spostamento')}</span>;
  if (chiedeEsito(visita, adesso) && immobile.stato_attivita !== 'scartato') {
    return (
      <span data-testid="esito-scheda">
        {t('immobili.scheda.esito_aspettiamo')} <Link className="link" to="/dashboard">{t('immobili.scheda.diteci')}</Link>
      </span>
    );
  }
  return <span data-testid="esito-scheda">{t('immobili.scheda.esito_non_dato')}</span>;
};

export const Visite = ({ immobile, visite, proposte, errore, consulente, adesso, onRichiesta, onRiprova, onRifiuto }) => {
  const { t, l } = useTraduzioni();
  const [chiedi, setChiedi] = useState(false);
  const ordinate = [...visite].filter((v) => !v.annullata && leggiData(v.data_visita))
    .sort((a, b) => leggiData(b.data_visita) - leggiData(a.data_visita));
  // senza l'elenco delle visite non si sa se ce n'è già una: niente prenotazioni (né dopo "Non fa per noi")
  const possibile = !errore && puoChiedereVisita(immobile, visite, adesso, proposte);
  // il pannello della richiesta c'è solo se si può ancora chiedere: dopo un rifiuto del server (lo staff ha
  // scartato la casa nel frattempo) la card restava col solo titolo, senza "Nessuna visita ancora."
  const pannello = chiedi && possibile;
  return (
    <section className="card r" data-testid="visite-immobile">
      <div className="ttl">
        <div className="i g"><Icona nome="calendario" /></div><h2>{t('immobili.scheda.visite')}</h2>
        {possibile && ordinate.length > 0 && !pannello && <a href="#visita" role="button" onClick={(e) => { e.preventDefault(); setChiedi(true); }}>{t('immobili.scheda.altra_visita')}</a>}
      </div>
      {errore && (
        <p className="vuoto-card" role="alert" data-testid="visite-non-disponibili">
          {t('immobili.scheda.visite_non_disponibili')}{' '}
          <a className="link" href="#riprova" onClick={(e) => { e.preventDefault(); onRiprova(); }}>{t('immobili.scheda.riprovate')}</a>.
        </p>
      )}
      {!errore && ordinate.length === 0 && !pannello && (
        <p className="vuoto-card">
          {t('immobili.scheda.nessuna_visita')}{' '}
          {possibile && <a className="link" href="#visita" onClick={(e) => { e.preventDefault(); setChiedi(true); }}>{t('immobili.scheda.prenota_visita_link')}</a>}
          {daCompilare(immobile) && t('immobili.scheda.visita_dopo_dati')}
        </p>
      )}
      {ordinate.length > 0 && (
        <div>
          {ordinate.map((v) => {
            const d = leggiData(v.data_visita);
            const esito = esitoVisita(v, l);
            const futura = d > adesso && !v.esito_visita;
            // chi ha accompagnato davvero (dal server, `con`); il consulente solo se manca
            const con = v.con || consulente.nome;
            return (
              <div className="vrow" key={v.id}>
                {/* con l'anno se la visita è di un altro anno (conAnno): prima "10 nov" sembrava di quest'anno */}
                <div className="d">{d.getDate()}<small>{conAnno(meseBreve(d, l), d, adesso)}</small></div>
                <div>
                  {/* la stessa forma della riga della card: "Visita giovedì 17 · 16:00 con Ernesto", "Visita oggi · 16:00…" */}
                  <b>{futura
                    ? (v.con ? t('immobili.scheda.visita_futura_con', { quando: quandoVisita(v.data_visita, adesso, l), nome: v.con })
                      : t('immobili.scheda.visita_futura', { quando: quandoVisita(v.data_visita, adesso, l) }))
                    : (con ? t('immobili.scheda.visita_fatta_con', { nome: con, ora: ora(d) }) : t('immobili.scheda.visita_fatta', { ora: ora(d) }))}</b>
                  {esito ? (
                    <span>{t('immobili.scheda.vostro_esito_prefisso')}<span className={`esito-${esito.classe}`}>{esito.testo}</span>{v.note_esito ? ` · “${v.note_esito}”` : ''}</span>
                  ) : !futura ? (
                    <SenzaEsito visita={v} immobile={immobile} adesso={adesso} />
                  ) : v.note_prima ? <span>“{v.note_prima}”</span> : null}
                  {futura && <AzioniVisita visita={v} immobile={immobile} consulente={consulente} onFatto={onRichiesta} onRifiuto={onRifiuto} />}
                  <MiniatureVisita visita={v} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {pannello && (
        <RispostaImmobile immobile={immobile} scelta="si" consulente={consulente} compatta
          onRifiuto={(messaggio) => { setChiedi(false); onRifiuto(messaggio); }}
          onAnnulla={() => setChiedi(false)} onFatto={() => { setChiedi(false); onRichiesta(); }} />
      )}
    </section>
  );
};

const Storia = ({ voci }) => {
  const { t } = useTraduzioni();
  if (!voci.length) return null;
  return (
  <section className="card r" data-testid="storia">
    <div className="ttl"><div className="i"><Icona nome="orologio" /></div><h2>{t('immobili.scheda.storia')}</h2></div>
    <div className="tl">
      {voci.map((v, i) => <div key={v.id || i} className={i === 0 ? 'c' : 'd'}><b>{v.testo}</b><span>{v.quando}</span></div>)}
    </div>
  </section>
  );
};

// «Avete altre 2 case con Ermes: via Manzoni 14 · via Cavour 8»: al cliente solo i nomi e i link, niente telefoni
// e niente bottoni (le telefonate e le visite in fila le fa lo staff — backend/agenzia_case.py)
const AltreCaseAgenzia = ({ immobile, agenzia }) => {
  const { tp } = useTraduzioni();
  const quante = immobile?.altre_con_agenzia || 0;
  const case_ = (immobile?.altre_case || []).filter((k) => k?.id);
  if (!quante || !case_.length) return null;
  return (
    <p className="nota-card" data-testid="altre-case">
      {tp('immobili.plurali.altre_case', quante, { agenzia: agenzia.nome })}{' '}
      {case_.map((k, n) => (
        <React.Fragment key={k.id}>
          {n > 0 && ' · '}
          <Link to={`/immobili/${k.id}`}>{k.indirizzo}</Link>
        </React.Fragment>
      ))}
    </p>
  );
};

const Agenzia = ({ agenzia, immobile }) => {
  const { t, l } = useTraduzioni();
  const [logoRotto, setLogoRotto] = useState(false);
  if (!agenzia?.nome) return null;
  const sotto = [agenzia.comune, agenzia.contatto && t('immobili.scheda.referente', { nome: agenzia.contatto })].filter(Boolean).join(' · ');
  return (
    <section className="card r" data-testid="agenzia">
      <div className="ttl"><div className="i b"><Icona nome="immobili" /></div><h2>{t('immobili.scheda.agenzia')}</h2></div>
      <div className="agc">
        <div className="lg">{agenzia.logo_url && !logoRotto ? <img src={agenzia.logo_url} alt="" onError={() => setLogoRotto(true)} /> : agenzia.nome.charAt(0).toUpperCase()}</div>
        <div>
          <b>{agenzia.nome}</b>
          {sotto && <span>{sotto}</span>}
          {agenzia.stato && <div style={{ marginTop: '.4rem' }}><span className={`pill ${agenzia.stato}`}>{testoAgenzia(agenzia.stato, l)}</span></div>}
        </div>
      </div>
      <AltreCaseAgenzia immobile={immobile} agenzia={agenzia} />
      <p className="nota-card">{t('immobili.scheda.parliamo_noi')}</p>
    </section>
  );
};

// Casa tolta dallo staff (non dal cliente): il perché, detto della casa. Prima diceva "Se cambiate idea, scrivete a
// Ernesto" anche per una casa già venduta, come se l'avesse scartata il cliente
const PERCHE_TOLTA = ['gia_venduto', 'agenzia_non_collaborativa', 'prezzo_alto', 'zona_non_adatta', 'condizioni_immobile',
  'metratura_inadeguata', 'piano_non_adatto', 'mancanza_requisiti', 'cliente_non_interessato'];
export const testoScartata = (immobile, nome, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  if (immobile.scartato_dal_cliente) {
    const motivo = motivoScartoBreve(immobile.motivo_scarto, lingua);
    return t(motivo ? 'immobili.scheda.tolta_dal_cliente_motivo' : 'immobili.scheda.tolta_dal_cliente', { motivo, nome });
  }
  const perche = PERCHE_TOLTA.includes(immobile.motivo_scarto) ? t(`immobili.perche_tolta.${immobile.motivo_scarto}`) : null;
  return t(perche ? 'immobili.scheda.tolta_motivo' : 'immobili.scheda.tolta', { motivo: perche, nome });
};

// Fuori dalla ricerca senza lo stato "scartato" (stessa regola del filtro "Scartati", fuoriRicerca): "Non fa per voi"
// dopo la visita o la proposta non accettata. Prima la scheda offriva ancora "Prenotate un'altra visita" e "Non ci
// interessa più" (che riavvisava lo staff), senza dire che la casa era tolta.
export const testoFuori = (immobile, etichetta, nome, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  if (immobile.stato_attivita === 'scartato') return testoScartata(immobile, nome, lingua);
  if (etichetta.testo === t('immobili.etichette.non_accettata')) return t('immobili.scheda.tolta_proposta', { nome });
  return t('immobili.scheda.tolta_visita', { nome });
};

// Perché "Non ci interessa più" non c'è (il server lo rifiuterebbe): cosa fare invece
const testoBlocco = (blocco, nome, t) => (['visita', 'proposta', 'accettata'].includes(blocco)
  ? t(`immobili.scheda.blocco_${blocco}`, { nome }) : null);

export const CosaPotetefare = ({ immobile, etichetta, visite, visiteErrore, proposte, consulente, cliente, adesso, decisioneInCima, onFatto, onRifiuto }) => {
  const { t, l } = useTraduzioni();
  const [aperto, setAperto] = useState(null); // 'si' | 'no'
  const dove = [immobile.indirizzo, immobile.citta].filter(Boolean).join(', ');
  const testo = t('immobili.scheda.messaggio_whatsapp', {
    consulente: consulente.nome,
    cliente: [cliente?.nome, cliente?.cognome].filter(Boolean).join(' '),
    immobile: `${titoloImmobile(immobile, l)}${dove ? ` (${dove})` : ''}`,
  });
  const wa = linkWhatsApp(consulente.telefono);
  // tolta dalla ricerca: la regola del filtro "Scartati" (anche "Non fa per voi" dopo la visita)
  const scartato = fuoriRicerca(etichetta);
  const possibile = !visiteErrore && puoChiedereVisita(immobile, visite, adesso, proposte);
  const blocco = scartato ? null : bloccoNonInteressa(immobile, visite, proposte, adesso);
  // dopo una rilettura (es. un 400 da un'altra finestra) il pannello aperto sparisce se non vale più. Anche con la
  // casa fuori dalla ricerca: `blocco` lì è sempre nullo, così il riquadro "Perché: … Invia" restava aperto e ogni
  // Invio dava un altro 400 ("questa casa è già fra quelle che non vi interessano")
  const pannello = (aperto === 'si' && possibile) || (aperto === 'no' && !blocco && !scartato) ? aperto : null;
  return (
    <section className="card r" data-testid="cosa-potete-fare">
      <div className="ttl"><div className="i w"><Icona nome="fatto" /></div><h2>{t('immobili.scheda.cosa_potete_fare')}</h2></div>
      <div className="azioni colonna">
        {wa && <a className="btn p" href={`${wa}?text=${encodeURIComponent(testo)}`} target="_blank" rel="noopener noreferrer">{t('immobili.scheda.chiedete', { nome: consulente.nome })}</a>}
        {!decisioneInCima && !scartato && possibile && pannello !== 'si' && (
          <button type="button" className="btn" onClick={() => setAperto('si')}>{t(visite.length ? 'immobili.scheda.altra_visita' : 'immobili.scheda.prenota_visita')}</button>
        )}
        {!decisioneInCima && !scartato && !blocco && pannello !== 'no' && (
          <button type="button" className="btn ghost-bad" onClick={() => setAperto('no')}>{t('immobili.scheda.non_interessa_piu')}</button>
        )}
      </div>
      {blocco && !decisioneInCima && (
        <p className="nota-azione" data-testid="nota-non-interessa" style={{ marginTop: '.9rem' }}>
          {testoBlocco(blocco, consulente.nome || t('comune.il_consulente'), t)}
        </p>
      )}
      {pannello && (
        <RispostaImmobile key={pannello} immobile={immobile} scelta={pannello} consulente={consulente} compatta onRifiuto={onRifiuto}
          onAnnulla={() => setAperto(null)} onFatto={() => { setAperto(null); onFatto(); }} />
      )}
      {scartato && <p className="nota-card" data-testid="nota-scartata">{testoFuori(immobile, etichetta, consulente.nome || t('comune.il_consulente'), l)}</p>}
    </section>
  );
};

// Prezzo e zona: le variazioni di prezzo lette dall'estrattore, €/m² della casa e della zona, riferimento OMI
const PrezzoEZona = ({ immobile }) => {
  const { t, l: lingua } = useTraduzioni();
  const zona = usePrezzoZona(immobile.id);
  const variazioni = variazioniPrezzo(immobile.storico_prezzi, lingua);
  const media = zona?.zona?.media;
  if (!variazioni.length && !media && !zona?.omi) return null;
  return (
    <section className="card r" data-testid="prezzo-zona">
      <div className="ttl"><div className="i"><Icona nome="euro" /></div><h2>{t('immobili.scheda.prezzo_zona')}</h2></div>
      {variazioni.length > 0 && (
        <div className="tl" style={{ marginBottom: '.8rem' }}>
          {variazioni.map((v, i) => <div key={i} className={i === 0 ? 'c' : 'd'}><b>{v.testo}</b></div>)}
        </div>
      )}
      {(zona?.mq_immobile || media) && (
        <div className="specs">
          {/* formatEuroMq e non euroMq: le cifre e il "м²" della sua lingua, come nell'elenco degli immobili */}
          {zona.mq_immobile && <div><b>{formatEuroMq(zona.mq_immobile, lingua)}</b><span>{t('immobili.scheda.questa_casa')}</span></div>}
          {media && <div><b>{formatEuroMq(media, lingua)}</b><span>{t('immobili.scheda.mq_zona')}</span></div>}
        </div>
      )}
      <p className="nota-card" style={{ fontSize: '.78rem', marginTop: '.8rem' }}>
        {media ? `${t('immobili.scheda.media_zona', { n: zona.zona.n })} ` : ''}
        {zona?.omi ? t('immobili.scheda.fonte_omi', { testo: testoOmi(zona.omi), fonte: zona.omi.fonte }) : ''}
      </p>
    </section>
  );
};

const SeLaComprate = ({ stima }) => {
  const { t, l, numero } = useTraduzioni();
  // senza una rata vera (prezzo 1 €: il server risponde disponibile: false) la card non c'è, mai cifre vuote
  if (!stima?.disponibile || !(stima.rata > 0) || !(stima.prezzo > 0)) return null;
  const tasso = numero(stima.tasso, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  return (
    <section className="card r" data-testid="se-la-comprate">
      <div className="ttl"><div className="i"><Icona nome="euro" /></div><h2>{t('immobili.scheda.se_la_comprate')}</h2></div>
      <div className="specs">
        <div><b>{formatEuro(stima.rata, l)}</b><span>{t('immobili.scheda.rata')}</span></div>
        <div><b>{formatEuro(stima.anticipo, l)}</b><span>{t('immobili.scheda.anticipo')}</span></div>
      </div>
      <p className="nota-card" style={{ fontSize: '.78rem', marginTop: '.8rem' }}>
        {t(stima.da_proposta ? 'immobili.scheda.calcolo_proposta' : 'immobili.scheda.calcolo', {
          prezzo: formatEuro(stima.prezzo, l),
          percentuale: stima.percentuale,
          anni: stima.anni,
          tasso: t(stima.tasso_di_riserva ? 'immobili.scheda.tasso_riserva' : 'immobili.scheda.tasso_oggi', { tasso }),
        })}
      </p>
    </section>
  );
};

const agenziaDa = (immobile, pubblica) => {
  if (immobile.agenzia_info) return immobile.agenzia_info;
  if (pubblica) {
    // la DICHIARAZIONE, non la collaborazione (come backend/server.py, stato_agenzia)
    const stato = pubblica.stato_dichiarato === 'dichiarato' ? 'dichiarata'
      : (pubblica.stato_dichiarato === 'non_dichiarato' || pubblica.stato_verifica === 'non_lavorabile') ? 'non_dichiara' : 'in_attesa';
    return { nome: pubblica.nome, comune: pubblica.comune, contatto: pubblica.contatto, logo_url: pubblica.logo_url, stato };
  }
  return immobile.agenzia ? { nome: immobile.agenzia, logo_url: immobile.agenzia_logo_url, stato: statoAgenzia(immobile) } : null;
};

// "Tutti gli immobili": se si arriva dall'elenco (la card passa filtro e ordinamento) si torna indietro nella
// cronologia, così l'elenco ritrova filtro, ordinamento e punto della pagina; altrimenti si apre /immobili
const Indietro = () => {
  const { t } = useTraduzioni();
  const { state } = useLocation();
  const navigate = useNavigate();
  const daElenco = typeof state?.daElenco === 'string' ? state.daElenco : null;
  return (
    <Link className="back" to={`/immobili${daElenco || ''}`}
      onClick={(e) => { if (daElenco !== null && window.history.length > 1) { e.preventDefault(); navigate(-1); } }}>
      <Icona nome="indietro" />{t('immobili.scheda.tutti')}
    </Link>
  );
};

// Una risposta del server è una scheda solo se è un immobile (un oggetto con l'id chiesto)
const eImmobile = (dato, id) => !!dato && typeof dato === 'object' && !Array.isArray(dato) && dato.id != null && String(dato.id) === String(id);
const lista = (esito) => (esito.status === 'fulfilled' && Array.isArray(esito.value.data) ? esito.value.data : []);

const SchedaImmobileCliente = () => {
  const { t, l } = useTraduzioni();
  const { id } = useParams();
  const { user } = useAuth();
  const consulente = useConsulente(user?.id);
  const [dati, setDati] = useState(null);
  const [stato, setStato] = useState('carica'); // carica | ok | nontrovato | errore
  const [avviso, setAvviso] = useState(null); // {testo, riprova}: scheda non aggiornata o azione rifiutata
  // l'ora di Roma (come le date delle visite), che avanza da sola: passata l'ora della visita la riga smette di
  // offrire Indicazioni e Sposta senza ricaricare, come nel calendario e in home
  const [adesso, setAdesso] = useState(adessoRoma);
  useEffect(() => {
    const t = setInterval(() => setAdesso(adessoRoma()), OGNI_MEZZO_MINUTO);
    return () => clearInterval(t);
  }, []);
  const haDati = useRef(false);

  // Una scheda nuova si apre dall'alto (arrivando dall'elenco la pagina era scesa)
  useEffect(() => { if (window.scrollY > 0) window.scrollTo(0, 0); }, [id]);

  // Vero se la scheda è aggiornata
  const carica = useCallback(async () => {
    const percorso = `${API}/immobili/${encodeURIComponent(id)}`;
    const [immobile, visite, storico, proposte, stima] = await Promise.allSettled([
      axios.get(percorso), axios.get(`${percorso}/visite`), axios.get(`${percorso}/storico`),
      axios.get(`${API}/clienti/${encodeURIComponent(user.id)}/proposte`), axios.get(`${percorso}/stima-mutuo`),
    ]);
    const codice = immobile.status === 'rejected' ? immobile.reason?.response?.status : null;
    if (codice === 403 || codice === 404 || (immobile.status === 'fulfilled' && !eImmobile(immobile.value.data, id))) {
      haDati.current = false;
      setStato('nontrovato');
      return false;
    }
    // Le proposte decidono l'etichetta ("Offerta accettata", "Proposta in corso"), la card "La vostra proposta",
    // "Non ci interessa più" e la rata: senza, la scheda inventava (Lela "Proposta in corso" invece dell'offerta
    // accettata, a Oleksandr spariva la card della proposta). Servono come l'immobile, come nell'elenco e in home.
    const proposteOk = proposte.status === 'fulfilled' && Array.isArray(proposte.value?.data);
    if (immobile.status === 'rejected' || !proposteOk) {
      // con la scheda già a video restano i dati di prima, con un avviso
      if (haDati.current) setAvviso({ testo: t('immobili.scheda.non_aggiornata'), riprova: true });
      else setStato('errore');
      return false;
    }
    const imm = immobile.value.data;
    let pubblica = null;
    if (!imm.agenzia_info && imm.agenzia_id) {
      try { pubblica = (await axios.get(`${API}/agenzie/${encodeURIComponent(imm.agenzia_id)}`)).data?.agenzia; } catch { /* senza agenzia */ }
    }
    const tutteProposte = lista(proposte);
    const offerta = propostaAttiva(tutteProposte, imm.id);
    const prezzoBase = offerta ? prezzoNumero({ prezzo_testo: offerta.prezzo_offerto }) : prezzoNumero(imm);
    const stimaServer = stima.status === 'fulfilled' && stima.value.data && typeof stima.value.data === 'object' ? stima.value.data : null;
    haDati.current = true;
    setAdesso(adessoRoma());
    setDati({
      immobile: imm,
      visite: lista(visite),
      visiteErrore: visite.status === 'rejected' || !Array.isArray(visite.value?.data),
      storico: lista(storico),
      proposte: tutteProposte,
      offerta,
      agenzia: agenziaDa(imm, pubblica),
      stima: stimaServer || { ...stimaLocale(prezzoBase), da_proposta: !!offerta },
    });
    setAvviso((a) => (a?.riprova ? null : a));
    setStato('ok');
    return true;
  }, [id, user?.id]);

  // Il server ha rifiutato un'azione (la casa è cambiata nel frattempo): si rileggono i dati e si dice perché
  const rifiutata = useCallback((messaggio) => {
    setAvviso({ testo: `${messaggio} ${t('immobili.scheda.aggiornata')}` });
    carica();
  }, [carica, t]);
  const fatto = useCallback(() => { setAvviso(null); carica(); }, [carica]);

  useEffect(() => {
    // un'altra scheda (o la prima): niente dati vecchi a video mentre si carica
    haDati.current = false;
    setAvviso(null);
    setDati(null);
    setStato('carica');
    carica();
  }, [carica]);
  useEffect(() => {
    const prima = document.title;
    if (dati) document.title = `MK Client — ${titoloImmobile(dati.immobile, l)}`;
    return () => { document.title = prima; };
  }, [dati]);

  const voci = useMemo(() => (dati ? storia(dati.storico, dati.immobile, adesso, l) : []), [dati, adesso, l]);

  if (stato !== 'ok') {
    return (
      <main className="mk-home pagina-immobili wrap">
        <Indietro />
        <div className="attesa" role="status">
          {stato === 'carica' && <span>{t('comune.caricamento')}</span>}
          {stato === 'nontrovato' && <div className="vuoto-elenco"><b>{t('immobili.scheda.non_trovato')}</b>{t('immobili.scheda.forse_tolto')}</div>}
          {stato === 'errore' && <div className="err">{t('immobili.scheda.errore')} <a href="#riprova" onClick={(e) => { e.preventDefault(); carica(); }}><u>{t('comune.riprova')}</u></a></div>}
        </div>
      </main>
    );
  }

  const { immobile, visite, visiteErrore, proposte, offerta, agenzia, stima } = dati;
  const etichetta = etichettaCliente(immobile, visite, proposte, adesso, l);
  const decidere = daDecidereOra(immobile, visite);
  const fuori = fuoriRicerca(etichetta); // tolta dalla ricerca (come il filtro "Scartati")
  const nonOnline = !!immobile.annuncio_non_online && !fuori;
  const prezzo = prezzoNumero(immobile);
  const mq = prezzoMq(immobile);
  const linkMappa = linkIndicazioni(immobile);
  const altri = altriPortali(immobile);  // «Anche su idealista.it · casa.it» (backend/doppioni.py)
  // "la vostra proposta", come la card "La vostra proposta" (prima "proposta nostra 362.000 €")
  const sottoPrezzo = [formatEuroMq(mq, l),
    offerta && t('immobili.scheda.la_vostra_proposta', { prezzo: formatEuro(prezzoNumero({ prezzo_testo: offerta.prezzo_offerto }), l) })].filter(Boolean).join(' · ');
  // casa appena aggiunta, dati dell'annuncio non ancora arrivati: come la card, niente "—" e niente prenotazione
  const compilare = daCompilare(immobile);

  return (
    <main className="mk-home pagina-immobili wrap" data-testid="scheda-immobile">
      <Indietro />
      <GalleriaFoto foto={fotoImmobile(immobile)} titolo={titoloImmobile(immobile, l)} className="r" />
      <div className="sh r">
        <div>
          <span className={`tag t-${etichetta.classe}`}>{etichetta.testo}</span>
          {/* asta giudiziaria (backend/aste.py): non si propone mai, ma se una casa così finisce in mano al
              cliente deve saperlo prima di chiedere la visita */}
          {immobile.asta && <span className="tag t-r" data-testid="tag-asta" style={{ marginLeft: '.3rem' }}>{t('immobili.scheda.asta')}</span>}
          <h1>{titoloImmobile(immobile, l)}</h1>
          {indirizzoCompleto(immobile) && <div className="dove">{indirizzoCompleto(immobile)}</div>}
          {compilare
            ? <div className="verifica" data-testid="dati-in-arrivo">{t('immobili.card.dati_in_arrivo')}</div>
            : <DatiImmobile immobile={immobile} />}
          {/* Dintorni subito sotto i dati della casa: pillole con i minuti a piedi, un clic apre nomi e mini-mappa */}
          <Dintorni immobile={immobile} />
          {/* tolta dalla ricerca: niente richiesta in corso (prima "Scartato" con sotto "Avete chiesto la visita") */}
          {immobile.visita_richiesta && !fuori && <div className="pending">{t('immobili.card.visita_richiesta', { nome: consulente.nome })}</div>}
          {nonOnline && (
            <div className="fuori-portale" data-testid="annuncio-non-online">{t('immobili.card.non_online')}</div>
          )}
          {immobile.asta && <div className="verifica" data-testid="avviso-asta">{t('immobili.scheda.asta_avviso')}</div>}
        </div>
        <div className="destra">
          {/* senza prezzo niente "—" isolato: "Prezzo in arrivo" finché mancano i dati, altrimenti "Prezzo non indicato" */}
          <div className="pr">
            {prezzo ? formatEuro(prezzo, l) : prezzoDaVerificare(immobile) ? <span className="da-verificare">{t('immobili.prezzi.da_verificare')}</span>
              : <span className="da-verificare" data-testid="prezzo-assente">{t(compilare ? 'immobili.prezzi.in_arrivo' : 'immobili.prezzi.non_indicato')}</span>}
            {sottoPrezzo && <small>{sottoPrezzo}</small>}
          </div>
          <div className="azioni" style={{ justifyContent: 'flex-end', marginTop: '.8rem' }}>
            <a className="btn sm" href={ensureAbsoluteUrl(immobile.link)} target="_blank" rel="noopener noreferrer">{t('immobili.card.annuncio')}</a>
            {linkMappa && <a className="btn sm" href={linkMappa} target="_blank" rel="noopener noreferrer">{t('comune.indicazioni')}</a>}
          </div>
          {/* la stessa casa pubblicata anche altrove: una proposta sola, con i link degli altri portali */}
          {altri.length > 0 && (
            <div className="dove" style={{ marginTop: '.5rem' }} data-testid="anche-su">
              {t('immobili.scheda.anche_su')}{' '}
              {altri.map((p, n) => (
                <React.Fragment key={p.link}>
                  {n > 0 && ' · '}
                  <a href={ensureAbsoluteUrl(p.link)} target="_blank" rel="noopener noreferrer">{p.nome}</a>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      {decidere && (
        <DecisioneScheda immobile={immobile} consulente={consulente} onFatto={fatto} onRifiuto={rifiutata}
          provenienza={inseritoDalloStaff(immobile) ? provenienza(immobile, adesso, l) : ''} />
      )}

      <div className="two">
        <div className="stack">
          <Parere immobile={immobile} consulente={consulente} adesso={adesso} />
          <LaVostraProposta proposte={proposte} immobileId={immobile.id} consulente={consulente} adesso={adesso} />
          <Visite immobile={immobile} visite={visite} proposte={proposte} errore={visiteErrore} consulente={consulente} adesso={adesso}
            onRichiesta={fatto} onRiprova={carica} onRifiuto={rifiutata} />
          <Storia voci={voci} />
        </div>
        <div className="stack">
          <Agenzia agenzia={agenzia} immobile={immobile} />
          <CosaPotetefare immobile={immobile} etichetta={etichetta} visite={visite} visiteErrore={visiteErrore} proposte={proposte} consulente={consulente}
            cliente={user} adesso={adesso} decisioneInCima={decidere} onFatto={fatto} onRifiuto={rifiutata} />
          <PrezzoEZona immobile={immobile} />
          {/* niente rata per una casa tolta dalla ricerca (venduta, scartata, "Non fa per voi") */}
          {!fuori && <SeLaComprate stima={stima} />}
        </div>
      </div>

      {/* avviso non bloccante in fondo allo schermo: resta visibile anche se l'azione era più giù */}
      {avviso && (
        <div className={classi('err avviso-pagina fisso', !avviso.riprova && 'info')} role="alert" data-testid="avviso-scheda">
          <span>{avviso.testo}</span>
          {avviso.riprova && <button type="button" onClick={() => carica()}>{t('comune.riprova')}</button>}
          <button type="button" onClick={() => setAvviso(null)} aria-label={t('comune.chiudi_avviso')}>{t('comune.chiudi')}</button>
        </div>
      )}
    </main>
  );
};

export default SchedaImmobileCliente;
